import { useRef } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { ManualPaymentService } from '@/api/manualPaymentService';
import { toast } from 'sonner';
import { getBuyPanelsPath } from '../route';
import paypalLogo from '@/res/logos/PayPal_Logo_2022/PayPal_Logo_Horizontal_Full_Color_RGB.png';

interface ManualPayPalPaymentFormProps {
  requestUuid: string;
  token?: string;
  setIsSubmitting?: (isSubmitting: boolean) => void;
  payIn4?: boolean;
  beforePayment?: () => Promise<boolean>;
}

export default function ManualPayPalPaymentForm({
  requestUuid,
  token,
  setIsSubmitting,
  payIn4,
  beforePayment,
}: ManualPayPalPaymentFormProps) {
  const submittedRef = useRef(false);
  const [{ isPending, isRejected, isResolved }] = usePayPalScriptReducer();
  const paypalButtonContainerRef = useRef<HTMLDivElement | null>(null);
  const routePrefix = getBuyPanelsPath(window.location.pathname, '');

  async function onClick(
    _data: Record<string, unknown>,
    actions: { resolve: () => Promise<void>; reject: () => Promise<void> }
  ) {
    const canProceed = beforePayment ? await beforePayment() : true;

    if (!canProceed) {
      setIsSubmitting?.(false);
      return actions.reject();
    }

    return actions.resolve();
  }

  async function createOrder() {
    const response = await ManualPaymentService.createPaypalOrder(requestUuid, token ?? undefined);
    submittedRef.current = true;
    return response.id;
  }

  async function onApprove(data: { orderID: string }) {
    try {
      setIsSubmitting?.(true);
      const response = await ManualPaymentService.capturePaypalOrder(requestUuid, data.orderID, token ?? undefined);

      if (!response.confirmed) {
        toast.info('Payment is still processing. Please refresh shortly.');
        return;
      }

      window.location.assign(
        `${window.location.origin}${routePrefix}/manual-payment/success?${new URLSearchParams({
          requestId: requestUuid,
          token: token ?? '',
        }).toString()}`
      );
    } catch (error) {
      console.error('Manual PayPal approval failed:', error);
      toast.error('Payment finalization failed.');
    } finally {
      setIsSubmitting?.(false);
    }
  }

  function onError(error: unknown) {
    console.error('Manual PayPal SDK error:', error);
    const message = error instanceof Error ? error.message : 'PayPal is having trouble. Please try again or use another method.';
    toast.error(message);
  }

  async function onCancel() {
    setIsSubmitting?.(false);
    if (submittedRef.current) {
      submittedRef.current = false;
      toast('Payment was cancelled.');
    }
  }

  const triggerPayPalButton = () => {
    try {
      const container = paypalButtonContainerRef.current;
      if (!container) return;
      const button = container.querySelector('button');
      if (button instanceof HTMLButtonElement) {
        button.click();
      }
    } catch (error) {
      console.error('Failed to trigger PayPal button programmatically', error);
    }
  };

  if (isRejected) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-md border border-red-200">
        PayPal is currently unavailable. Please refresh or try another method.
      </div>
    );
  }

  if (isPending || !isResolved) {
    return <div className="h-12 flex items-center justify-center animate-pulse text-slate-400">Syncing with PayPal…</div>;
  }

  return (
    <div className="space-y-4 w-full flex justify-center">
      <div className="w-full max-w-md">
        {payIn4 ? (
          <>
            <div className="relative w-full">
              <div
                ref={paypalButtonContainerRef}
                style={{ position: 'absolute', inset: 0, opacity: 0, zIndex: 20 }}
                aria-hidden={false}
              >
                <PayPalButtons
                  style={{ layout: 'vertical', shape: 'rect', height: 45, color: 'gold' }}
                  onClick={onClick}
                  createOrder={async () => await createOrder()}
                  onApprove={async (data) => await onApprove({ orderID: data.orderID })}
                  onError={onError}
                  onCancel={onCancel}
                />
              </div>

              <button
                type="button"
                onClick={async () => {
                  const canProceed = beforePayment ? await beforePayment() : true;
                  if (!canProceed) {
                    return;
                  }

                  setIsSubmitting?.(true);
                  triggerPayPalButton();
                }}
                className="w-full rounded-lg flex items-center justify-center gap-3 py-3 bg-[#33f0d9] hover:brightness-95 z-10 relative"
                aria-label="Pay in 4 via PayPal"
              >
                <img src={paypalLogo} alt="PayPal" className="h-6 object-contain" />
                <span className="text-white font-semibold text-lg">Pay in 4</span>
              </button>
            </div>
          </>
        ) : (
          <div className="w-full">
            <PayPalButtons
              style={{ layout: 'vertical', shape: 'rect', height: 45, color: 'gold' }}
              onClick={onClick}
              createOrder={async () => await createOrder()}
              onApprove={async (data) => await onApprove({ orderID: data.orderID })}
              onError={onError}
              onCancel={onCancel}
            />
          </div>
        )}
      </div>
    </div>
  );
}
